/**
 * 라이더 → 협력사 일차감 페이지
 *
 * 협력사가 라이더에게 부과하는 일차감 관리
 * - 조회: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='S'
 * - 본사: 협력사 선택 후 조회 / 협력사: 바로 자신의 라이더 관리
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getItem } from '@/shared/utils/storage';
import SupplierSelectionPage from '@/features/partner/supplierStatus/components/SupplierSelectionPage';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SearchableSelect from '@/shared/components/forms/SearchableSelect';
import DateInput from '@/shared/components/forms/DateInput';
import { formatCurrency } from '@/shared/utils/formatter';
import { useRiderToPartnerData } from '../hooks/useRiderToPartnerData';
import { formatPeriod } from '../utils/riderToPartnerHelpers';
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';
import '../styles/DeductionRiderToSupplierPage.css';

const DeductionRiderToSupplierPage: React.FC = () => {
    const [userType, setUserType] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [selectedSupplierName, setSelectedSupplierName] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const userTypeValue = getItem<string>('USER_TYPE');
        const userId = getItem<string>('USER_ID');
        setUserType(userTypeValue || '');
        if (userTypeValue === 'SUPPLIER' && userId) setSelectedSupplierId(userId);
        setIsInitialized(true);
    }, []);

    const handleSupplierSelect = useCallback((supplierId: string, supplierName: string) => {
        setSelectedSupplierId(supplierId);
        setSelectedSupplierName(supplierName);
    }, []);

    const handleBackToSelection = useCallback(() => {
        setSelectedSupplierId('');
        setSelectedSupplierName('');
    }, []);

    if (!isInitialized) return <Loading message="사용자 정보를 확인하는 중..." />;
    if (userType === 'HEADQUARTERS' && !selectedSupplierId) {
        return (
            <SupplierSelectionPage
                onSupplierSelect={handleSupplierSelect}
                pageTitle="라이더 → 협력사 일차감"
            />
        );
    }

    return (
        <RiderToSupplierContent
            supplierId={selectedSupplierId}
            supplierName={selectedSupplierName}
            userType={userType}
            onBack={handleBackToSelection}
        />
    );
};

// ── 일차감 관리 본문 ──────────────────────────────────────────────────────────
interface ContentProps {
    supplierId: string;
    supplierName: string;
    userType: string;
    onBack: () => void;
}

const RiderToSupplierContent: React.FC<ContentProps> = ({ supplierId, supplierName, userType, onBack }) => {
    const {
        loading,
        ridersLoading,
        deductionTypesLoading,
        deductions,
        deductionTypes,
        filterRiderId,
        setFilterRiderId,
        newDeduction,
        setNewDeduction,
        pagination,
        handleSearch,
        handleDelete,
        handleStartDateChange,
        handleEndDateChange,
        handleDeductDaysChange,
        handleAdd,
        riderOptions,
        getRiderLabel,
        getDeductionTypeName,
        previewDays,
        previewTotal,
    } = useRiderToPartnerData(supplierId);

    const { currentPage, totalPages, currentItems, goToPage } = pagination;
    const ITEMS_PER_PAGE = 10;

    if (loading && deductions.length === 0) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">라이더 → 협력사 일차감</h1>
                </div>
                <Loading message="일차감 내역을 불러오는 중..." />
            </div>
        );
    }

    return (
        <div className="supplier-selection-page">
            <div className="page-header">
                {userType === 'HEADQUARTERS' && (
                    <button className="back-btn" type="button" onClick={onBack}>
                        <i className="fas fa-arrow-left" />
                        <span>협력사 목록으로</span>
                    </button>
                )}
                <h1 className="page-title">
                    라이더 → 협력사 일차감
                    {userType === 'HEADQUARTERS' && supplierName && (
                        <span className="supplier-name-badge">{supplierName}</span>
                    )}
                </h1>
            </div>

            {/* 필터 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form rider-to-supplier-search-form">
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-motorcycle" /> 라이더
                                </label>
                                <SearchableSelect
                                    options={riderOptions}
                                    value={filterRiderId}
                                    onChange={setFilterRiderId}
                                    placeholder={ridersLoading ? '로딩 중...' : '라이더 선택'}
                                    disabled={ridersLoading}
                                    showAllOption
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                        <div className="form-group button-group">
                            <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                                <i className="fas fa-search" /> 조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 */}
            <div className="rider-to-supplier-table-container">
                <table className="supplier-selection-table" style={{ minWidth: '1350px', tableLayout: 'auto' }}>
                    <thead>
                    <tr>
                        <th style={{ width: 60 }}>NO</th>
                        <th style={{ width: 250 }}>대상</th>
                        <th style={{ width: 120 }}>공제타입</th>
                        <th style={{ width: 200 }}>공제명</th>
                        <th style={{ width: 150 }}>공제금액</th>
                        <th style={{ width: 490 }}>기간 / 차감 일수</th>
                        <th style={{ width: 80 }}>등록</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.length === 0 && !loading ? (
                        <tr>
                            <td colSpan={7} className="text-center text-gray-600">
                                조회된 일차감 내역이 없습니다.
                            </td>
                        </tr>
                    ) : (
                        currentItems.map((d, idx) => (
                            <tr key={d.seqNo}>
                                <td className="text-center">{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                                <td>{getRiderLabel(d.riderId)}</td>
                                <td className="text-center">{getDeductionTypeName(d.deductType)}</td>
                                <td>{d.deductName}</td>
                                <td className="text-right">{formatCurrency(String(d.deductAmt || 0))}원</td>
                                <td className="text-center">
                                    {formatPeriod(d.startDay, d.endDay)}
                                    {d.deductCnt != null && (
                                        <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>
                                                ({d.deductCnt}일)
                                            </span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <button className="btn-delete" onClick={() => handleDelete(d.seqNo)}>
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}

                    {/* 새 일차감 등록 행 */}
                    <tr className="add-row">
                        <td className="text-center text-gray-600">+</td>
                        <td>
                            <SearchableSelect
                                options={riderOptions}
                                value={newDeduction.riderId}
                                onChange={(v) => setNewDeduction((p) => ({ ...p, riderId: v }))}
                                placeholder={ridersLoading ? '로딩 중...' : '선택'}
                                disabled={ridersLoading}
                                showAllOption
                                allOptionLabel="선택"
                            />
                        </td>
                        <td>
                            <select
                                className="form-select"
                                value={newDeduction.deductionTypeCode}
                                onChange={(e) => {
                                    const code = e.target.value;
                                    const type = deductionTypes.find((t) => t.code === code);
                                    setNewDeduction((p) => ({
                                        ...p,
                                        deductionTypeCode: code,
                                        deductionName: type ? type.name : '',
                                    }));
                                }}
                                disabled={deductionTypesLoading}
                                style={{ width: '100%' }}
                            >
                                <option value="">{deductionTypesLoading ? '로딩 중...' : '선택'}</option>
                                {deductionTypes.map((t) => (
                                    <option key={t.code} value={t.code}>{t.name}</option>
                                ))}
                            </select>
                        </td>
                        <td>
                            <input
                                type="text"
                                className="filter-input"
                                value={newDeduction.deductionName}
                                onChange={(e) => setNewDeduction((p) => ({ ...p, deductionName: e.target.value }))}
                                placeholder="공제명"
                                style={{ width: '100%' }}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                className="filter-input"
                                value={formatCurrency(newDeduction.deductionAmount)}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setNewDeduction((p) => ({ ...p, deductionAmount: value }));
                                }}
                                placeholder="금액"
                                style={{ textAlign: 'right' }}
                            />
                        </td>
                        <td>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <DateInput id="newStartDate" value={newDeduction.startDate} onChange={handleStartDateChange} />
                                <span>~</span>
                                <DateInput id="newEndDate" value={newDeduction.endDate} onChange={handleEndDateChange} />
                                <input
                                    type="number"
                                    min="1"
                                    className="filter-input"
                                    value={newDeduction.deductDays}
                                    onChange={(e) => handleDeductDaysChange(e.target.value)}
                                    placeholder="일수"
                                    style={{ width: 80, textAlign: 'center' }}
                                />
                                <span style={{ fontSize: 12, color: '#555' }}>일</span>
                                {previewDays > 0 && (
                                    <span style={{ fontSize: 11, color: '#1a73e8' }}>
                                            {previewTotal > 0
                                                ? `(총 ${formatCurrency(String(previewTotal))}원)`
                                                : `(${previewDays}일)`}
                                        </span>
                                )}
                            </div>
                        </td>
                        <td className="text-center">
                            <button className="btn btn-primary btn-register" onClick={handleAdd}>
                                <i className="fas fa-plus-circle" />
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} />
        </div>
    );
};

export default DeductionRiderToSupplierPage;
